import { habilidadeLabels } from './utils.js';

const notInformed = 'Não informado.';

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function asList(value) {
  if (Array.isArray(value)) return value.map((item) => clean(item?.acao || item?.item || item)).filter(Boolean);
  const text = clean(value);
  return text ? [text] : [];
}

function firstText(...values) {
  for (const value of values) {
    const text = clean(value);
    if (text) return text;
  }
  return '';
}

function skillNames(activity) {
  return (activity?.habilidades_alvo || [])
    .map((id) => habilidadeLabels[id] || id)
    .filter(Boolean);
}

function sourceText(activity) {
  if (clean(activity?.fonte)) return clean(activity.fonte);
  const source = activity?.fonteDetalhada;
  if (!source || typeof source !== 'object') return notInformed;
  return [source.titulo, source.autoria, source.url].filter(Boolean).join(' — ') || notInformed;
}

function generalizationText(activity) {
  const explicit = firstText(activity?.generalizacao, activity?.estrategia_de_generalizacao);
  if (explicit) return explicit;
  const contexts = asList(activity?.variacoes?.contexto);
  if (contexts.length > 1) {
    return `Após estabilidade, reaplique com outro material, pessoa ou ambiente. Contextos previstos: ${contexts.join(', ')}.`;
  }
  return 'Após estabilidade, teste a mesma habilidade com outro material, pessoa ou ambiente e registre se o apoio diminuiu.';
}

function mainAdaptation(activity) {
  return firstText(
    activity?.adaptacao_principal,
    activity?.progressao?.simplificar,
    activity?.adaptacoes?.orientacao,
    Object.values(activity?.adaptacoes_por_perfil || {})[0],
  ) || 'Ajuste quantidade de estímulos, forma de resposta e nível de apoio conforme a observação do estudante.';
}

export function buildActivityPedagogicalCard(activity = {}) {
  const skills = skillNames(activity);
  const whenToUse = firstText(activity.quando_indicar, activity.indicacao_pedagogica)
    || (skills.length
      ? `Quando a meta do PAEE/PEI envolver ${skills.join(', ')} e a observação mostrar uma barreira relacionada.`
      : 'Quando a meta do PAEE/PEI e a observação direta mostrarem relação com a habilidade trabalhada.');
  const rationale = firstText(activity.por_que_funciona, activity.fundamentacao?.resumo, activity.justificativa_pedagogica)
    || `Oferece prática estruturada de ${skills.join(', ') || 'uma habilidade-alvo'}, permitindo observar respostas e ajustar apoios. Esta justificativa pedagógica não equivale a validação científica específica da atividade.`;
  const authorship = firstText(activity.autoria, activity.curadoria, activity.responsavel_pedagogico)
    || (activity.custom ? 'Atividade local da escola; autoria individual não informada.' : 'Curadoria do SGAEE; autoria individual não informada.');
  const validation = firstText(activity.situacao_validacao, activity.validacao?.situacao)
    || 'Situação de validação externa não informada.';

  return {
    objective: firstText(activity.objetivo, activity.objetivo_operacional) || notInformed,
    whenToUse,
    steps: asList(activity.roteiro?.length ? activity.roteiro : activity.passo_a_passo),
    howToRecord: firstText(activity.registro?.como, activity.como_medir_progresso) || notInformed,
    mainAdaptation: mainAdaptation(activity),
    materials: asList(activity.materiais),
    whyWorks: rationale,
    prerequisites: asList(activity.prerequisitos),
    simplify: firstText(activity.progressao?.simplificar) || notInformed,
    progress: firstText(activity.progressao?.ampliar, activity.progressao?.progredir) || notInformed,
    inadequacySigns: asList(activity.sinais_de_inadequacao),
    commonErrors: asList(activity.erros_comuns_do_aplicador),
    generalization: generalizationText(activity),
    authorship,
    source: sourceText(activity),
    validation,
    skills,
    criterion: firstText(activity.criterio_de_dominio) || notInformed,
  };
}

export function activityToLogText(activity) {
  const card = buildActivityPedagogicalCard(activity);
  return `${activity?.titulo || 'Atividade'}
Objetivo: ${card.objective}
Quando indicar: ${card.whenToUse}
Materiais: ${card.materials.join(', ') || '-'}
Passos: ${card.steps.map((step, index) => `${index + 1}. ${step}`).join(' | ') || '-'}
Como registrar: ${card.howToRecord}
Adaptação principal: ${card.mainAdaptation}
Fonte: ${card.source}
Validação: ${card.validation}`;
}
